<template>
  <div
    v-viewer
    class="upload-viewer"
    :style="isUseObs ? 'min-height: 60px' : ''"
  >
    <template v-if="isUseObs">
      <div
        v-for="(item, index) in defaultList"
        :key="index"
        :draggable="max !== PICTURE_NUM && draggable"
        @dragstart="handleDragStart($event, index)"
        @dragenter.prevent
        @dragover.prevent
        @drop="handleDragEnd($event, index)"
      >
        <template v-if="!item.status || item.status === 'success'">
          <div class="upload-viewer__pic">
            <img
              v-if="uploadType === 'image'"
              :src="item.url"
              class="upload-viewer__pic--img"
            />
            <video
              v-if="uploadType === 'video'"
              :src="item.url"
              class="upload-viewer__pic--img"
            />
            <i
              v-if="uploadType === 'video'"
              class="el-icon-video-play upload-viewer__pic--play"
              @click="handlePlayVideo(item, index)"
            />
            <i
              v-if="!$attrs.disabled"
              class="el-icon-circle-close upload-viewer__pic--icon"
              @click="handleDel(item, index)"
            />
          </div>
        </template>
        <template v-else-if="item.percentage && item.percentage !== 100">
          <div class="upload-viewer__pic">
            <div class="upload-viewer__pic--progress">
              <el-progress
                :percentage="Number(item.percentage.toFixed(2))"
                status="success"
                hide-info
              ></el-progress>
            </div>
          </div>
        </template>
      </div>
    </template>
    <el-upload
      v-show="$slots.default || isUploadShow"
      ref="upload"
      :show-file-list="false"
      :action="fullApiAction || action"
      :data="isUseObs || useObsData ? obsUploadData : {}"
      :multiple="max !== PICTURE_NUM"
      :limit="max"
      :type="isUseObs ? 'drag' : 'select'"
      :file-list="defaultList"
      :accept="ACCEPT_MAP[uploadType] || ''"
      :before-upload="handleBeforeUpload"
      :on-success="handleSuccess"
      :on-exceed="handleExceed"
      :on-error="handleError"
      :with-credentials="
        !['image', 'video'].includes(uploadType) &&
        action.indexOf('yishou-img') === -1 &&
        !actionApi
      "
      v-bind="$attrs"
    >
      <div v-if="!$slots.default" class="upload-viewer__placeholder">
        <i
          :class="iconMap[uploadType] || 'el-icon-camera-solid'"
          style="font-size: 26px"
        />
        <span v-if="max" class="upload-viewer__placeholder--text">
          {{ defaultList.length }}/{{ max }}
        </span>
      </div>
      <slot v-else />
    </el-upload>
    <!-- 视频弹窗 -->
    <div
      v-if="isShowModalVideoPlay"
      class="video-modal"
      @click="isShowModalVideoPlay = false"
    >
      <video :src="videoSrc" controls style="height: 70vh" @click.stop />
      <div class="video-modal__text" @click.stop="handleCopy(videoSrc)">
        <span
          >若视频无法播放，可直接点击复制下方视频地址在其他浏览器或者手机上打开</span
        >
        <span>视频地址：{{ videoSrc }}</span>
      </div>
    </div>
  </div>
</template>
<script>
import md5 from "js-md5";
import * as dayjs from "dayjs";
import { mapGetters } from "vuex";
import apiBaseURL from "@/utils/baseUrl.js";

/**
 * 用于提取数组对象中的某一属性值，组成新数组返回
 * eg: filterKeyList([{name: "名称", url: "链接"}], name) 输出 ["名称"]
 * @param arr
 * @param key
 * @return {*}
 */
const filterKeyList = (arr, key) => {
  return key ? arr.map((item) => item[key]) : arr;
};
export default {
  name: "YsUpload",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: [String, Array],
      default: "",
    },
    /**
     * 简写的上次类型，format可以覆盖此属性
     */
    uploadType: {
      type: String,
      validator: (value) => {
        return ["image", "video", "excel"].includes(value);
      },
      default: "image",
    },
    /**
     * 上传目录
     */
    prefix: {
      type: String,
      default: "",
    },
    /**
     * 最大上传数量
     */
    max: {
      type: Number,
      default: 0,
    },
    /**
     * 是否显示超出最大数量限制提示
     */
    showMessage: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否支持拖拽排序
     */
    draggable: {
      type: Boolean,
      default: false,
    },
    /**
     * url别名
     */
    urlAlias: {
      type: String,
      default: "url",
    },
    /**
     * 过滤key名，可以直接更改value的值形式
     * 传入的列表有多种格式，如["imgUrl"]、[{ url: String }]
     * 传出默认为iView的格式[{ name: "", status: "", percent: "", url: "" }]
     */
    filterKey: {
      type: String,
      validator: (value) => {
        return ["name", "url", ""].includes(value);
      },
      default: "",
    },
    /**
     * 是否显示错误通知
     */
    showNotice: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否使用Obs的data
     */
    useObsData: {
      type: Boolean,
      default: false,
    },
    /**
     * api请求地址
     */
    actionApi: {
      type: String,
      default: "",
    },
    /**
     * el-upload 组件属性
     */
    uploadAttr: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      // 图片上传限制数量
      PICTURE_NUM: 1,
      // 默认大小限制，视频 300M，图片 2M
      SIZE_MAP: {
        video: 307200,
        image: 2048,
      },
      ACCEPT_MAP: {
        image: "image/*",
        video: "video/*",
        excel:
          ".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      FORMAT_MAP: {
        image: ["jpg", "jpeg", "png", "gif", "svg"],
        video: ["mp4", "flv", "mov", "swf"],
        excel: ["csv", "xlsx", "xls"],
      },
      iconMap: {
        image: "el-icon-camera-solid",
        video: "el-icon-video-camera",
        excel: "el-icon-folder-opened",
      },
      action: "",
      obsUploadData: {},
      defaultList: [],
      uploadList: [],
      isInit: true, // 控制是否更新 defaultList，iView 组件对 defaultFileList 做了深层监听，所以不能随便更新
      isShowMessageCount: 0, // 触发提示次数
      startIndex: 0,
      endIndex: 0,
      isShowModalVideoPlay: false,
      videoSrc: "",
    };
  },
  computed: {
    ...mapGetters({
      uploadData: "obsUploadData",
    }),
    isUploadShow() {
      return !this.max || (this.max && this.defaultList.length < this.max);
    },
    isUseObs() {
      return ["image", "video"].includes(this.uploadType);
    },
    fullApiAction() {
      return this.actionApi ? `${apiBaseURL}${this.actionApi}` : "";
    },
  },
  watch: {
    value: {
      handler(val) {
        const isArray = Array.isArray(val);
        if (this.isInit) {
          if (isArray) {
            this.defaultList = val.map((item) => {
              return typeof item === "string"
                ? {
                    name: this.handleGetNameFormUrl(item),
                    url: !/^http/.test(item)
                      ? `${this.obsUploadData.domain}${item}`
                      : item,
                  }
                : {
                    ...item,
                    name: item.name
                      ? this.handleGetNameFormUrl(item.name)
                      : this.handleGetNameFormUrl(item[this.urlAlias]),
                    url: item[this.urlAlias],
                  };
            });
          } else if (val) {
            this.defaultList = [
              {
                name: this.handleGetNameFormUrl(val),
                url:
                  val && !/^http/.test(val)
                    ? `${this.obsUploadData.domain}${val}`
                    : val,
              },
            ];
          } else {
            this.defaultList = [];
          }
        }
        this.$nextTick(() => {
          this.isInit = true;
          if (isArray) {
            this.isShowMessageCount = val.length;
          } else if (val) {
            this.isShowMessageCount = 1;
          } else {
            this.isShowMessageCount = 0;
          }
        });
      },
      immediate: true,
    },
    isShowMessageCount: {
      handler(val) {
        if (
          (this.isUseObs || this.useObsData) &&
          this.showMessage &&
          this.max &&
          val > this.max
        ) {
          this.$message.info(`所选文件数量超出限制，最多 ${this.max} 张/个`);
        }
      },
      immediate: true,
    },
    uploadData: {
      handler(val) {
        const { bucket_name: bucketName, endpoint, uptoken } = val;
        const {
          access_key_id: accessKeyId,
          domain,
          policy,
          signature,
          success_action_status: successActionStatus,
        } = uptoken || {};
        this.obsUploadData = {
          AccessKeyId: accessKeyId,
          domain,
          policy,
          signature,
          success_action_status: successActionStatus,
        };
        this.action = `https://${bucketName}.${endpoint}/`;
      },
      immediate: true,
    },
  },
  mounted() {
    const { bucket_name: bucketName, endpoint, uptoken } = this.uploadData;
    const {
      access_key_id: accessKeyId,
      domain,
      policy,
      signature,
      success_action_status: successActionStatus,
    } = uptoken || {};
    this.obsUploadData = {
      AccessKeyId: accessKeyId,
      domain,
      policy,
      signature,
      success_action_status: successActionStatus,
    };
    this.action = `https://${bucketName}.${endpoint}/`;
  },
  methods: {
    // public
    clearFiles() {
      this.$refs.upload.clearFiles();
      this.$emit("input", "");
    },
    handleGetNameFormUrl(url) {
      return url.replace(this.obsUploadData.domain, "");
    },
    handleBeforeUpload(file) {
      if (this.isUseObs || this.useObsData) {
        this.isInit = false;
        this.isShowMessageCount += 1;
        // 当前数量已达到最大值，则禁止上传
        if (this.max && this.$refs.upload.fileList.length >= this.max) {
          if (this.uploadType === "excel") {
            this.isInit = true;
          }
          return false;
        }
        const random = md5(`${file.name}${Date.now()}`);
        const customPath = this.prefix.replace(/^\/*|\/*$/g, "")
          ? `${this.prefix.replace(/^\/*|\/*$/g, "")}/`
          : "";
        const suffix = file.name.toLowerCase().split(".").splice(-1);
        this.obsUploadData.key = `obs/${customPath}${dayjs().format(
          "YYYY/MM/DD"
        )}/${random}.${suffix}`;
        this.$emit("on-before-upload", file);
      }
      return true;
    },
    handleSuccess(res, file, fileList) {
      if (this.isUseObs || this.useObsData) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(res, "text/xml");
        const tmpKey = xmlDoc.querySelector("Key").innerHTML;
        const currentFileList = fileList;
        // eslint-disable-next-line no-param-reassign
        file.name = tmpKey;
        // eslint-disable-next-line no-param-reassign
        file.url = `${this.obsUploadData.domain}${tmpKey}`;
        this.defaultList = currentFileList;
        this.isInit = false;
        if (Array.isArray(this.value)) {
          this.$emit("input", filterKeyList(this.defaultList, this.filterKey));
        } else {
          this.$emit("input", file.name);
        }
      }
      this.$emit("on-success", res, file, fileList);
    },
    handleDel(file) {
      const { fileList } = this.$refs.upload;
      this.$refs.upload.fileList.splice(fileList.indexOf(file), 1);
      this.isInit = false;
      if (Array.isArray(this.value)) {
        this.$emit(
          "input",
          filterKeyList(this.$refs.upload.fileList, this.filterKey)
        );
      } else {
        this.$emit("input", "");
      }
    },
    handleExceed(files, fileList) {
      this.$message.info(
        `最多上传 ${this.max} 个文件，本次选择了 ${files.length} 个文件，已有 ${fileList.length} 个文件`
      );
    },
    handlePlayVideo(item) {
      this.videoSrc = item.url;
      this.isShowModalVideoPlay = true;
    },
    handleDragStart(e, index) {
      this.startIndex = index;
    },
    handleDragEnd(e, index) {
      this.endIndex = index;
      if (this.startIndex === this.endIndex) {
        return;
      }
      const { fileList } = this.$refs.upload;
      const startItem = fileList[this.startIndex];
      const endItem = fileList[this.endIndex];
      this.$refs.upload.fileList.splice(this.startIndex, 1, endItem);
      this.$refs.upload.fileList.splice(this.endIndex, 1, startItem);
      this.isInit = false;
      this.$emit(
        "input",
        filterKeyList(this.$refs.upload.fileList, this.filterKey)
      );
    },
    handleExceededSize(...rest) {
      this.isShowMessageCount -= 1;
      if (this.showNotice) {
        this.$notify.warning(
          `文件大小不能超过 ${
            (this.$attrs.maxSize ||
              (this.uploadType === "video"
                ? this.SIZE_MAP.video
                : this.SIZE_MAP.image)) / 1024
          }Mb`
        );
      }
      this.$emit("on-exceeded-size", ...rest);
    },
    handleError(...rest) {
      this.isShowMessageCount -= 1;
      this.$emit("on-error", ...rest);
    },
    handleFormatError(...rest) {
      this.isShowMessageCount -= 1;
      const typeList = this.$attrs.format || this.FORMAT_MAP[this.uploadType];
      if (this.showNotice) {
        this.$notify.warning(
          `上传文件类型错误, 仅支持${typeList.join("、")}的文件格式`
        );
      }
      this.$emit("on-format-error", ...rest);
    },
    handleCopy(text) {
      this.$copyText(text).then(() => {
        this.$message.success("复制成功！");
      });
    },
  },
};
</script>
<style scoped>
.upload-viewer {
  display: flex;
  align-items: center;
  flex-flow: row wrap;
  cursor: pointer;
}

.upload-viewer__pic {
  position: relative;
  width: 60px;
  height: 60px;
  padding: 5px 10px 5px 0;
}

.upload-viewer__pic--img {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  object-fit: cover;
  cursor: pointer;
  border: 1px dashed rgb(220, 222, 226);
}

.upload-viewer__pic--icon {
  position: absolute;
  right: 0;
  top: -4px;
  color: #f13685;
}

.upload-viewer__pic--play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #f13685;
}

.upload-viewer__pic--progress {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  border: 1px dashed rgb(220, 222, 226);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-viewer__placeholder {
  width: 60px;
  height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgb(220, 222, 226);
}

.upload-viewer__placeholder--text {
  font-size: 12px;
  line-height: 12px;
}

.video-modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 60%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.video-modal__text {
  line-height: 30px;
  font-size: 16px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/YsComponents/YsUpload.vue